import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import OrderChat from '../components/OrderChat';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Package, MapPin, CreditCard, Clock, CheckCircle, 
  Truck, XCircle, User, Store, ChevronRight, Loader2
} from 'lucide-react';

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isFuncionario = user?.role === 'station_admin';

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const orderRes = await api().get(`/orders/${id}`);
        setOrder(orderRes.data);
      } catch (err) {
        toast.error('Pedido não encontrado');
        navigate(isFuncionario ? '/admin' : '/orders');
      }
      setLoading(false);
    };
    loadOrder();
  }, [api, id, navigate, isFuncionario]);

  const updateOrderStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api().patch(`/orders/${id}/status?status=${newStatus}`);
      setOrder(prev => ({ ...prev, status: newStatus }));
      toast.success('Estado actualizado!');
    } catch (err) {
      toast.error('Erro ao actualizar estado');
    }
    setUpdatingStatus(false);
  };

  const statusConfig = {
    pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    preparing: { label: 'Em Preparação', color: 'bg-blue-100 text-blue-700', icon: Package },
    ready: { label: 'Pronto', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    in_delivery: { label: 'Em Entrega', color: 'bg-purple-100 text-purple-700', icon: Truck },
    completed: { label: 'Concluído', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  const getNextStatus = (current) => {
    const flow = { pending: 'preparing', preparing: 'ready', ready: 'in_delivery', in_delivery: 'completed' };
    return flow[current];
  };

  const paymentLabels = {
    multicaixa: 'Multicaixa Express',
    mobile_money: 'Mobile Money',
    cash: 'Dinheiro'
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-AO', { 
      day: '2-digit', 
      month: 'long',
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isFuncionario ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
        <div className={`animate-spin w-8 h-8 border-4 ${isFuncionario ? 'border-sonangol-orange' : 'border-sonangol-red'} border-t-transparent rounded-full`}></div>
      </div>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const nextStatus = getNextStatus(order.status);

  // Progress steps
  const steps = ['pending', 'preparing', 'ready', order.delivery_method === 'delivery' ? 'in_delivery' : null, 'completed'].filter(Boolean);
  const currentStepIndex = steps.indexOf(order.status);

  // Funcionario View
  if (isFuncionario) {
    return (
      <div className="min-h-screen bg-zinc-900 pb-20">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-zinc-800 border-b border-zinc-700 px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-700 text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-barlow font-bold text-lg text-white">Pedido #{order.id.slice(0, 8)}</h1>
              <p className="text-xs text-zinc-400">{formatDate(order.created_at)}</p>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 space-y-4">
          {/* Status Card with Actions */}
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow font-bold text-white">Estado do Pedido</h2>
              <Badge className={`${status.color} border-0`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
            </div>
            
            {nextStatus && order.status !== 'completed' && order.status !== 'cancelled' && (
              <Button
                className="w-full h-12 bg-sonangol-orange hover:bg-orange-600 font-barlow font-bold uppercase"
                onClick={() => updateOrderStatus(nextStatus)}
                disabled={updatingStatus}
              >
                {updatingStatus ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Avançar para {statusConfig[nextStatus]?.label}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Order Items */}
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
            <h2 className="font-barlow font-bold text-white mb-3">Produtos</h2>
            <div className="space-y-2">
              {order.items.map((item) => (
                <div key={item.product_id} className="flex justify-between py-2 border-b border-zinc-700 last:border-0">
                  <span className="text-zinc-300">{item.quantity}x {item.name}</span>
                  <span className="text-white font-bold">{item.subtotal.toLocaleString('pt-AO')} AOA</span>
                </div>
              ))}
              <div className="pt-2 flex justify-between font-barlow font-bold text-lg">
                <span className="text-white">Total</span>
                <span className="text-sonangol-orange">{order.total_aoa.toLocaleString('pt-AO')} AOA</span>
              </div>
            </div>
          </div>

          {/* Delivery Info */}
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
            <h2 className="font-barlow font-bold text-white mb-3">Detalhes</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-sonangol-orange" />
                <div>
                  <p className="text-white">{order.delivery_method === 'pickup' ? 'Recolha' : 'Entrega'}</p>
                  <p className="text-zinc-400 text-xs">{order.station_name}</p>
                </div>
              </div>
              {order.address && (
                <div className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-white">Endereço</p>
                    <p className="text-zinc-400 text-xs">{order.address}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-white">{paymentLabels[order.payment_method]}</p>
                  <p className="text-zinc-400 text-xs">Método de pagamento</p>
                </div>
              </div>
            </div>
          </div>

          {/* Chat Component */}
          <div className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
            <OrderChat orderId={id} orderStatus={order.status} />
          </div>

          {/* Cancel Button */}
          {order.status !== 'completed' && order.status !== 'cancelled' && (
            <Button
              variant="outline"
              className="w-full h-12 border-red-500/50 text-red-400 hover:bg-red-500/10"
              onClick={() => updateOrderStatus('cancelled')}
              disabled={updatingStatus}
            >
              <XCircle className="w-5 h-5 mr-2" />
              Cancelar Pedido
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Client View
  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-zinc-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/orders')} 
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-barlow font-bold text-lg">Pedido #{order.id.slice(0, 8)}</h1>
            <p className="text-xs text-zinc-500">{formatDate(order.created_at)}</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Status Card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-barlow font-bold text-lg">Estado do Pedido</h2>
            <Badge className={`${status.color} border-0`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
          </div>
          
          {/* Progress Bar */}
          <div className="flex items-center gap-2">
            {steps.map((step, idx) => {
              const isCompleted = idx <= currentStepIndex;
              const isCurrent = idx === currentStepIndex;
              return (
                <React.Fragment key={step}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? 'bg-sonangol-red text-white' : 'bg-zinc-200 text-zinc-400'
                  } ${isCurrent ? 'ring-4 ring-red-100' : ''}`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`flex-1 h-1 rounded ${idx < currentStepIndex ? 'bg-sonangol-red' : 'bg-zinc-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Order Info */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
          <h2 className="font-barlow font-bold text-lg mb-4">Detalhes</h2>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-sonangol-red flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-zinc-800">{order.station_name}</p>
                <p className="text-xs text-zinc-500">
                  {order.delivery_method === 'pickup' ? 'Recolha na bomba' : 'Entrega ao domicílio'}
                </p>
                {order.address && (
                  <p className="text-xs text-zinc-600 mt-1">{order.address}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-sonangol-orange flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-zinc-800">{paymentLabels[order.payment_method]}</p>
                <p className="text-xs text-zinc-500">Método de pagamento</p>
              </div>
            </div>

            {order.notes && (
              <div className="bg-zinc-50 rounded-xl p-3 mt-3">
                <p className="text-xs text-zinc-500 mb-1">Notas:</p>
                <p className="text-sm text-zinc-700">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Products */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
          <h2 className="font-barlow font-bold text-lg mb-4">Produtos</h2>
          <div className="space-y-3">
            {order.items.map((item) => (
              <div key={item.product_id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                <div>
                  <p className="font-medium text-zinc-800">{item.name}</p>
                  <p className="text-xs text-zinc-500">{item.quantity}x {item.price_aoa.toLocaleString('pt-AO')} AOA</p>
                </div>
                <p className="font-barlow font-bold text-zinc-800">
                  {item.subtotal.toLocaleString('pt-AO')} AOA
                </p>
              </div>
            ))}
            <div className="pt-2 flex justify-between font-barlow font-bold text-lg">
              <span>Total</span>
              <span className="text-sonangol-red">{order.total_aoa.toLocaleString('pt-AO')} AOA</span>
            </div>
          </div>
        </div>

        {/* Chat Component */}
        <OrderChat orderId={id} orderStatus={order.status} />
      </div>

      <BottomNav />
    </div>
  );
};

export default OrderDetailPage;
